// src/receptionist/transcripts.ts
//
// Learning a merchant's flow and voice from chats they already had.
//
// This is the fastest route to a configured receptionist, and it has a
// precedent in this codebase: the TechnoMech hiring process map (see
// docs/adr/chat-native-whatsapp-hiring-flow.md) was reconstructed BY HAND from
// three real WhatsApp threads. This automates that reconstruction.
//
// The hard rule is that nothing is applied automatically. The analysis
// produces a PROPOSAL the merchant reviews field by field, because a wrong
// price or a mis-read policy would then be repeated to every customer.
//
// Privacy: transcripts are other people's conversations. They carry customer
// names and numbers, so the contract is narrow — used once to configure this
// merchant, never for model training, redacted on ingest, deleted after the
// retention window. Malaysia's PDPA applies.

export const TRANSCRIPT_RETENTION_DAYS = 30;

export type ProposalKind = "step" | "tone" | "language" | "faq" | "service" | "policy";

export interface Proposal {
    id: string;
    kind: ProposalKind;
    /** What we would set. */
    title: string;
    /** The value itself, shown so the merchant can check it. */
    detail: string;
    /** How many uploaded threads support this. */
    evidenceCount: number;
    /** A real line from a thread, so the merchant can see where it came from. */
    quote?: string;
    /** Low confidence items default to unchecked. */
    confidence: "high" | "medium" | "low";
    accepted: boolean;
}

export interface TranscriptFile {
    id: string;
    name: string;
    /** Messages parsed out of the export. */
    messageCount: number;
    status: "parsed" | "parsing" | "failed";
    error?: string;
}

export const PROPOSAL_KIND_LABELS: Record<ProposalKind, string> = {
    step: "Booking step",
    tone: "Tone",
    language: "Language",
    faq: "FAQ note",
    service: "Service",
    policy: "Policy",
};

/**
 * Stand-in for the backend analysis. The real one reads the parsed threads and
 * returns this shape; the UI contract is what matters here.
 */
export function demoProposals(): Proposal[] {
    return [
        {
            id: "p1", kind: "step", title: "Ask which treatment first",
            detail: "Every thread opens by establishing the service before anything else.",
            evidenceCount: 12, confidence: "high", accepted: true,
            quote: "Hi, nak buat facial. Yang mana ok untuk oily skin?",
        },
        {
            id: "p2", kind: "step", title: "Ask whether it is a first visit",
            detail: "Asked in 9 of 12 threads, usually right after the treatment is settled.",
            evidenceCount: 9, confidence: "high", accepted: true,
            quote: "First time dengan kami ke?",
        },
        {
            id: "p3", kind: "step", title: "Ask for a preferred day and time",
            detail: "Always asked, and customers routinely give a range rather than a slot.",
            evidenceCount: 12, confidence: "high", accepted: true,
        },
        {
            id: "p4", kind: "tone", title: "Salon casual",
            detail: "Short sentences, frequent exclamation marks, occasional emoji. Not formal.",
            evidenceCount: 12, confidence: "high", accepted: true,
            quote: "Boleh! Kita ada slot 3pm 😊",
        },
        {
            id: "p5", kind: "language", title: "Bahasa Melayu and English, mixed",
            detail: "Both appear in the same message in 7 of 12 threads. Chinese in 1.",
            evidenceCount: 12, confidence: "high", accepted: true,
        },
        {
            id: "p6", kind: "faq", title: "Parking",
            detail: "Asked in 5 threads. Answer given: basement parking, Zone B lifts.",
            evidenceCount: 5, confidence: "medium", accepted: true,
            quote: "Parking senang tak kat situ?",
        },
        {
            id: "p7", kind: "policy", title: "Deposit for treatments over RM300",
            detail: "Mentioned once. Wording was inconsistent with the price list, so worth checking.",
            evidenceCount: 1, confidence: "low", accepted: false,
            quote: "Untuk peel kena deposit RM50 dulu ya",
        },
        {
            id: "p8", kind: "service", title: "Eyebrow embroidery, RM450",
            detail: "Quoted in 2 threads but not on your services list.",
            evidenceCount: 2, confidence: "medium", accepted: false,
        },
    ];
}
